import { useEffect, useState, useRef, useMemo } from 'react';
import { matchesProjectFilter, isActiveProject } from '../lib/projectStatus';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { CalendarDays, Plus, Trash2, Edit2, Clock, CheckCircle2, AlertCircle, PlayCircle, ClipboardList, ListTodo, MessageSquare, ExternalLink, RotateCcw, Camera, Image as ImageIcon, X, ChevronDown, ChevronRight, FileSpreadsheet, Check, CheckSquare, UserPlus, Users, Send } from 'lucide-react';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import ProgressSlider from '@/components/ProgressSlider';
import TaskCommentsSidebar from '@/components/TaskCommentsSidebar';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-stone-100 text-stone-600', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-600', icon: PlayCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-600', icon: CheckCircle2 },
  delayed: { label: 'Delayed', color: 'bg-red-100 text-red-600', icon: AlertCircle },
};

const TASK_STATUS_CONFIG = {
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-600' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-600' },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-600' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-600' },
  on_hold: { label: 'On Hold', color: 'bg-red-100 text-red-600' },
};

export default function Schedule() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [combinedSchedule, setCombinedSchedule] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('all');
  // iter-338z8: Active/Inactive project filter — matches Projects.js.
  const [projectActiveFilter, setProjectActiveFilter] = useState('active');
  const [viewTab, setViewTab] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState(false);
  const [selectedTaskForComments, setSelectedTaskForComments] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // P0 NEW: Chat dialog state for task subject linking
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedTaskForChat, setSelectedTaskForChat] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  
  // P0 NEW: Multi-vendor assignment state
  const [assignVendorsDialogOpen, setAssignVendorsDialogOpen] = useState(false);
  const [selectedTaskForVendors, setSelectedTaskForVendors] = useState(null);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  
  // P2 NEW: Bulk vendor assignment state
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkSelectedVendorIds, setBulkSelectedVendorIds] = useState([]);
  const [bulkAvailableVendors, setBulkAvailableVendors] = useState([]);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'staff', 'admin'].includes(user.role);
  
  // Image upload state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedTaskForImage, setSelectedTaskForImage] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  
  // Tasks for BOQ generation
  const [tasks, setTasks] = useState([]);
  
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    assigned_to: '',
    status: 'not_started',
    dependencies: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [combinedRes, activitiesRes, projectsRes, usersRes, tasksRes] = await Promise.all([
        axios.get(`${API}/schedule/combined`).catch(() => ({ data: [] })),
        axios.get(`${API}/activities`),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/tasks`).catch(() => ({ data: [] })),
      ]);
      setCombinedSchedule(combinedRes.data);
      setActivities(activitiesRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  // Generate BOQ from approved tasks
  const handleGenerateBOQ = async (projectId) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets/from-project/${projectId}`);
      toast.success(`BOQ ${response.data.worksheet_number} created with ${response.data.items?.length || 0} items`);
      // Navigate to BOQ Worksheets page after successful creation
      navigate('/boq-worksheets');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate BOQ');
    }
  };

  // Bulk approve tasks
  const handleBulkApprove = async () => {
    // Get only task items (not activities) from selection - use 'source' not 'type'
    // The combined schedule prefixes task IDs with "task_", so we need to extract the original ID
    const taskIds = Array.from(selectedItems)
      .filter(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item && item.source === 'task';
      })
      .map(id => {
        // Extract original task ID (remove "task_" prefix)
        const item = filteredCombined.find(i => i.id === id);
        return item?.task_id || id.replace('task_', '');
      });
    
    if (taskIds.length === 0) {
      toast.error('No tasks selected for approval');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/bulk-approve`, { task_ids: taskIds });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve tasks');
    }
  };

  // Bulk reject tasks
  const handleBulkReject = async () => {
    const taskIds = Array.from(selectedItems)
      .filter(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item && item.source === 'task';
      })
      .map(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item?.task_id || id.replace('task_', '');
      });
    
    if (taskIds.length === 0) {
      toast.error('No tasks selected for rejection');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/bulk-reject`, { task_ids: taskIds });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject tasks');
    }
  };

  // Bulk revert to pending approval
  const handleBulkRevertPending = async () => {
    const taskIds = Array.from(selectedItems)
      .filter(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item && item.source === 'task';
      })
      .map(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item?.task_id || id.replace('task_', '');
      });
    
    if (taskIds.length === 0) {
      toast.error('No tasks selected');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/bulk-revert-pending`, { task_ids: taskIds });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert tasks');
    }
  };

  // Bulk status update
  const handleBulkStatusUpdate = async (newStatus) => {
    const taskIds = Array.from(selectedItems)
      .filter(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item && item.source === 'task';
      })
      .map(id => {
        const item = filteredCombined.find(i => i.id === id);
        return item?.task_id || id.replace('task_', '');
      });
    
    if (taskIds.length === 0) {
      toast.error('No tasks selected for status update');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/bulk-status-update`, { 
        task_ids: taskIds,
        status: newStatus 
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task status');
    }
  };

  // Status options for bulk update
  const bulkStatusOptions = [
    { value: 'not_started', label: 'Not Started', color: 'text-stone-600' },
    { value: 'pending_approval', label: 'Pending Approval', color: 'text-amber-600' },
    { value: 'in_progress', label: 'In Progress', color: 'text-blue-600' },
    { value: 'completed', label: 'Completed', color: 'text-green-600' },
    { value: 'delayed', label: 'Delayed', color: 'text-red-600' },
    { value: 'completed_with_snags', label: 'Completed - Snags Pending', color: 'text-amber-700' },
    { value: 'completed_with_extra_works', label: 'Completed - Extra Works Pending', color: 'text-orange-600' },
    { value: 'fully_completed', label: '100% Completed', color: 'text-emerald-600' },
  ];

  const handleCreate = async () => {
    if (!formData.project_id || !formData.name) {
      toast.error('Project and activity name are required');
      return;
    }

    try {
      await axios.post(`${API}/activities`, {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
      });
      toast.success('Activity created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create activity');
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/activities/${selectedActivity.id}`, {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
      });
      toast.success('Activity updated successfully');
      setEditDialogOpen(false);
      setSelectedActivity(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update activity');
    }
  };

  const handleStatusChange = async (activity, newStatus) => {
    try {
      await axios.put(`${API}/activities/${activity.id}`, { status: newStatus });
      toast.success(`Activity marked as ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleProgressChange = async (item, progress, isTask = false) => {
    try {
      // Auto-update status based on progress
      let newStatus = item.status;
      if (progress === 0) newStatus = 'not_started';
      else if (progress === 100) newStatus = 'completed';
      else if (progress > 0 && (item.status === 'not_started' || !item.status)) newStatus = 'in_progress';
      
      if (isTask) {
        // Use task_id (real ID) not item.id (which has task_ prefix)
        const realTaskId = item.task_id || item.id.replace('task_', '');
        
        // Auto-approve task if progress is being updated and task is not approved
        const updateData = { 
          progress: progress,
          status: newStatus,
        };
        
        // If task is not approved and progress > 0, auto-approve it
        if (progress > 0 && !item.approved) {
          updateData.approved = true;
        }
        
        await axios.put(`${API}/tasks/${realTaskId}`, updateData);
        toast.success(`Task progress updated to ${progress}%`);
      } else {
        // Update activity progress
        await axios.put(`${API}/activities/${item.id}`, { 
          progress: progress,
          status: newStatus,
        });
        toast.success(`Activity progress updated to ${progress}%`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update progress');
    }
  };

  // Toggle task approval status (approve/unapprove)
  const handleToggleApproval = async (item) => {
    try {
      const realTaskId = item.task_id || item.id.replace('task_', '');
      const newApproved = !item.approved;
      
      await axios.put(`${API}/tasks/${realTaskId}`, { 
        approved: newApproved,
        status: newApproved ? 'in_progress' : 'not_started',
        progress: newApproved ? item.progress : 0,
      });
      
      toast.success(newApproved ? 'Task approved' : 'Task reverted to pending');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Navigate to task details
  const handleTaskClick = (item) => {
    const realTaskId = item.task_id || item.id.replace('task_', '');
    navigate(`/tasks?task=${realTaskId}`);
  };

  // Image upload handlers
  const openImageDialog = (item) => {
    const realTaskId = item.task_id || item.id.replace('task_', '');
    setSelectedTaskForImage({
      id: realTaskId,
      title: item.name,
      project_id: item.project_id,
    });
    setImageCaption('');
    setImageDialogOpen(true);
  };

  const handleFileSelect = async (event, isCamera = false) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTaskForImage) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Url = e.target.result;
        
        try {
          await axios.post(`${API}/tasks/${selectedTaskForImage.id}/images`, {
            task_id: selectedTaskForImage.id,
            project_id: selectedTaskForImage.project_id,
            filename: file.name,
            url: base64Url,
            caption: imageCaption || null,
          });
          
          toast.success('Image uploaded successfully');
          setImageDialogOpen(false);
          setSelectedTaskForImage(null);
          setImageCaption('');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploading(false);
    }
    
    // Reset file input
    event.target.value = '';
  };

  // P0 NEW: Open Chat Dialog with Task Subject
  const openChatWithTask = async (item) => {
    // Extract real task_id (item.id might have 'task_' prefix)
    const realTaskId = item.task_id || item.id.replace('task_', '');
    setSelectedTaskForChat({
      ...item,
      task_id: realTaskId
    });
    setChatMessage('');
    setChatDialogOpen(true);
  };

  // P0 NEW: Send Chat Message linked to Task
  const handleSendChatMessage = async () => {
    if (!chatMessage.trim() || !selectedTaskForChat) return;
    
    setSendingChat(true);
    try {
      await axios.post(`${API}/projects/${selectedTaskForChat.project_id}/chat`, {
        project_id: selectedTaskForChat.project_id,
        message: chatMessage,
        visibility: 'internal',
        task_id: selectedTaskForChat.task_id,
        task_name: selectedTaskForChat.name  // Match backend model field name
      });
      
      toast.success('Message sent');
      setChatDialogOpen(false);
      setChatMessage('');
      setSelectedTaskForChat(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingChat(false);
    }
  };

  // P0 NEW: Open Multi-Vendor Assignment Dialog
  const openAssignVendorsDialog = async (item) => {
    // Extract real task_id (item.id might have 'task_' prefix)
    const realTaskId = item.task_id || item.id.replace('task_', '');
    setSelectedTaskForVendors({
      ...item,
      task_id: realTaskId
    });
    setSelectedVendorIds(item.assigned_vendors || (item.assigned_to ? [item.assigned_to] : []));
    
    // Get vendors for this project
    try {
      const project = projects.find(p => p.id === item.project_id);
      if (project && project.assigned_vendors) {
        const projectVendors = users.filter(u => 
          (u.role === 'vendor' || u.role === 'labour') && 
          project.assigned_vendors.includes(u.id)
        );
        setAvailableVendors(projectVendors);
      } else {
        setAvailableVendors([]);
      }
    } catch (error) {
      setAvailableVendors([]);
    }
    
    setAssignVendorsDialogOpen(true);
  };

  // P0 NEW: Assign Multiple Vendors to Task
  const handleAssignMultipleVendors = async () => {
    if (!selectedTaskForVendors || selectedVendorIds.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/${selectedTaskForVendors.task_id}/assign-vendors`, {
        vendor_ids: selectedVendorIds
      });
      
      toast.success(response.data.message || 'Vendors assigned successfully');
      setAssignVendorsDialogOpen(false);
      setSelectedTaskForVendors(null);
      setSelectedVendorIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign vendors');
    }
  };

  // Toggle vendor selection
  const toggleVendorSelection = (vendorId) => {
    setSelectedVendorIds(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  // P2 NEW: Open Bulk Vendor Assignment Dialog
  const openBulkAssignDialog = () => {
    // Get all selected tasks (only tasks, not activities)
    const selectedTasks = filteredCombined.filter(item => 
      selectedItems.has(item.id) && item.source === 'task'
    );
    
    if (selectedTasks.length === 0) {
      toast.error('Please select at least one task');
      return;
    }
    
    // Get unique project IDs from selected tasks
    const projectIds = [...new Set(selectedTasks.map(t => t.project_id))];
    
    // Find vendors assigned to ANY of these projects
    const allVendors = new Set();
    projectIds.forEach(projectId => {
      const project = projects.find(p => p.id === projectId);
      if (project && project.assigned_vendors) {
        project.assigned_vendors.forEach(v => allVendors.add(v));
      }
    });
    
    const projectVendors = users.filter(u => 
      (u.role === 'vendor' || u.role === 'labour') && 
      allVendors.has(u.id)
    );
    
    setBulkAvailableVendors(projectVendors);
    setBulkSelectedVendorIds([]);
    setBulkAssignDialogOpen(true);
  };

  // P2 NEW: Handle Bulk Vendor Assignment
  const handleBulkAssignVendors = async () => {
    if (bulkSelectedVendorIds.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }
    
    const selectedTasks = filteredCombined.filter(item => 
      selectedItems.has(item.id) && item.source === 'task'
    );
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const task of selectedTasks) {
        try {
          const realTaskId = task.task_id || task.id.replace('task_', '');
          await axios.post(`${API}/tasks/${realTaskId}/assign-vendors`, {
            vendor_ids: bulkSelectedVendorIds
          });
          successCount++;
        } catch (err) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Vendors assigned to ${successCount} task(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to assign ${failCount} task(s)`);
      }
      
      setBulkAssignDialogOpen(false);
      setBulkSelectedVendorIds([]);
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast.error('Failed to assign vendors');
    }
  };

  // Toggle bulk vendor selection
  const toggleBulkVendorSelection = (vendorId) => {
    setBulkSelectedVendorIds(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/activities/${selectedActivity.id}`);
      toast.success('Activity deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete activity');
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      name: '',
      description: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      assigned_to: '',
      status: 'not_started',
      dependencies: [],
    });
  };

  const openEditDialog = (activity) => {
    setSelectedActivity(activity);
    setFormData({
      project_id: activity.project_id,
      name: activity.name,
      description: activity.description || '',
      start_date: new Date(activity.start_date).toISOString().split('T')[0],
      end_date: new Date(activity.end_date).toISOString().split('T')[0],
      assigned_to: activity.assigned_to || '',
      status: activity.status,
      dependencies: activity.dependencies || [],
    });
    setEditDialogOpen(true);
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'N/A';
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.id === userId);
    return u?.name || 'Unassigned';
  };

  // iter-338z8: build a set of project ids visible under Active/Inactive/All.
  const visibleProjectIds = useMemo(() => new Set(projects.filter((p) => {
        return matchesProjectFilter(p, projectActiveFilter);
  }).map((p) => p.id)), [projects, projectActiveFilter]);

  const filteredActivities = (selectedProject === 'all'
    ? activities
    : activities.filter(a => a.project_id === selectedProject)
  ).filter(a => visibleProjectIds.has(a.project_id));

  // Filter combined schedule based on project, view tab, and status
  const filteredCombined = combinedSchedule.filter(item => {
    const matchesProject = selectedProject === 'all' || item.project_id === selectedProject;
    const matchesActive = visibleProjectIds.has(item.project_id);
    const matchesTab = viewTab === 'all' || 
      (viewTab === 'tasks' && item.source === 'task') ||
      (viewTab === 'activities' && item.source === 'activity');
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesProject && matchesActive && matchesTab && matchesStatus;
  });

  // Group combined items by project for timeline view
  const groupedByProject = filteredCombined.reduce((acc, item) => {
    const projectId = item.project_id;
    if (!acc[projectId]) acc[projectId] = [];
    acc[projectId].push(item);
    return acc;
  }, {});

  // Selection handlers
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleProjectSelection = (projectId) => {
    const projectItems = groupedByProject[projectId] || [];
    const projectItemIds = projectItems.map(i => i.id);
    const allSelected = projectItemIds.every(id => selectedItems.has(id));
    
    const newSelected = new Set(selectedItems);
    if (allSelected) {
      projectItemIds.forEach(id => newSelected.delete(id));
    } else {
      projectItemIds.forEach(id => newSelected.add(id));
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredCombined.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredCombined.map(i => i.id)));
    }
  };

  const toggleProjectExpand = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const isProjectExpanded = (projectId) => expandedProjects.has(projectId);
  const isProjectAllSelected = (projectId) => {
    const projectItems = groupedByProject[projectId] || [];
    return projectItems.length > 0 && projectItems.every(i => selectedItems.has(i.id));
  };

  // Calculate counts
  const taskCount = combinedSchedule.filter(i => i.source === 'task').length;
  const activityCount = combinedSchedule.filter(i => i.source === 'activity').length;

  // Calculate timeline metrics
  const getTimelinePosition = (activity) => {
    const today = new Date();
    const startRaw = activity.start_date ? new Date(activity.start_date) : null;
    const endRaw = activity.end_date ? new Date(activity.end_date) : null;
    
    // Fallback: if no valid start, use today; if no valid end, use start + 7 days
    const start = (startRaw && !isNaN(startRaw.getTime())) ? startRaw : today;
    const end = (endRaw && !isNaN(endRaw.getTime())) ? endRaw : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    const totalDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    const daysFromToday = Math.ceil((start - today) / (1000 * 60 * 60 * 24));
    return { totalDays, daysFromToday, start, end };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="schedule-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-2">
              Activity Schedule
            </h1>
            <p className="text-stone-500">Track activities and tasks across all projects</p>
          </div>
          {(user.role === 'owner' || user.role === 'staff') && (
            <Button onClick={() => setCreateDialogOpen(true)} className="apple-button" data-testid="create-activity-btn">
              <Plus size={18} className="mr-2" />
              New Activity
            </Button>
          )}
          
          {/* Generate BOQ Button - for owner/staff only */}
          {(user.role === 'owner' || user.role === 'staff') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100" data-testid="generate-boq-button">
                  <FileSpreadsheet size={18} />
                  Generate BOQ
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium text-stone-900">Select Project</p>
                  <p className="text-xs text-stone-500">Creates BOQ from approved tasks</p>
                </div>
                {projects.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-stone-400">
                    No projects found
                  </div>
                ) : (
                  projects.map((project) => {
                    const approvedCount = tasks.filter(t => t.project_id === project.id && t.approved && !t.in_boq).length;
                    return (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => handleGenerateBOQ(project.id)}
                        className="flex justify-between items-center"
                        disabled={approvedCount === 0}
                      >
                        <span className="truncate">{project.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${approvedCount > 0 ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}`}>
                          {approvedCount} approved
                        </span>
                      </DropdownMenuItem>
                    );
                  })
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Filters & Tabs */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <Tabs value={viewTab} onValueChange={setViewTab} className="w-full md:w-auto">
            <TabsList className="bg-stone-100">
              <TabsTrigger value="all" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
                <CalendarDays size={16} />
                All ({filteredCombined.length})
              </TabsTrigger>
              <TabsTrigger value="tasks" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
                <ListTodo size={16} />
                Tasks ({taskCount})
              </TabsTrigger>
              <TabsTrigger value="activities" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
                <ClipboardList size={16} />
                Activities ({activityCount})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {[...projects]
                .filter(p => {
                                    return matchesProjectFilter(p, projectActiveFilter);
                })
                .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                .map((p, idx) => <SelectItem key={p.id} value={p.id}>{idx + 1}. {p.name}{p.is_active === false ? ' · Inactive' : ''}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* iter-338z8: Active / Inactive / All project filter — matches Projects.js */}
          <div className="inline-flex items-center rounded-md border border-stone-300 overflow-hidden text-xs" data-testid="schedule-project-active-filter">
            {[
              { v: 'active', label: 'Active' },
              { v: 'inactive', label: 'Inactive' },
              { v: 'all', label: 'All' },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => { setProjectActiveFilter(opt.v); if (selectedProject !== 'all') setSelectedProject('all'); }}
                className={`px-3 py-1.5 font-medium transition-colors ${projectActiveFilter === opt.v ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                data-testid={`schedule-project-active-filter-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk Action Bar - Sticky */}
        {selectedItems.size > 0 && (
          <div className="sticky top-0 z-50 p-4 bg-blue-50 border border-blue-200 rounded-xl flex flex-wrap items-center justify-between gap-4 shadow-md" data-testid="schedule-bulk-action-bar">
            <div className="flex items-center gap-3">
              <CheckSquare className="text-blue-700" size={20} />
              <span className="font-medium text-stone-900">{selectedItems.size} item(s) selected</span>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <Button variant="outline" size="sm" onClick={() => setSelectedItems(new Set())} className="text-stone-700 border-stone-300 hover:bg-stone-100">
                <X size={16} className="mr-1" />
                Clear
              </Button>
              <Button size="sm" onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700 text-white">
                <Check size={16} className="mr-1" />
                Approve All
              </Button>
              
              {/* Bulk Status Update Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50">
                    <PlayCircle size={16} className="mr-1" />
                    Update Status
                    <ChevronDown size={14} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {bulkStatusOptions.map((option) => (
                    <DropdownMenuItem 
                      key={option.value}
                      onClick={() => handleBulkStatusUpdate(option.value)}
                      className={`cursor-pointer ${option.color}`}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button size="sm" variant="outline" onClick={handleBulkReject} className="text-orange-600 border-orange-300 hover:bg-orange-50">
                <X size={16} className="mr-1" />
                Reject All
              </Button>
              <Button size="sm" variant="outline" onClick={handleBulkRevertPending} className="text-purple-600 border-purple-300 hover:bg-purple-50">
                <RotateCcw size={16} className="mr-1" />
                Revert to Pending
              </Button>
              
              {/* P2 NEW: Bulk Vendor Assignment Button (Admin only) */}
              {isAdmin && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={openBulkAssignDialog} 
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  data-testid="bulk-assign-vendors-btn"
                >
                  <Users size={16} className="mr-1" />
                  Assign Vendors
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Summary Cards - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div 
            className="apple-card p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setViewTab('all')}
          >
            <div className="flex items-center gap-3 mb-2">
              <CalendarDays size={20} className="text-stone-800" />
              <span className="text-2xl font-bold">{filteredCombined.length}</span>
            </div>
            <p className="text-sm text-stone-500">Total Items</p>
          </div>
          <div 
            className="apple-card p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setViewTab('tasks')}
          >
            <div className="flex items-center gap-3 mb-2">
              <ListTodo size={20} className="text-amber-500" />
              <span className="text-2xl font-bold">{filteredCombined.filter(i => i.source === 'task').length}</span>
            </div>
            <p className="text-sm text-stone-500">Tasks</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'not_started' ? 'ring-2 ring-stone-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'not_started' ? 'all' : 'not_started')}
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} className="text-stone-500" />
              <span className="text-2xl font-bold">{combinedSchedule.filter(a => a.status === 'not_started').length}</span>
            </div>
            <p className="text-sm text-stone-500">Not Started</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'in_progress' ? 'ring-2 ring-blue-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? 'all' : 'in_progress')}
          >
            <div className="flex items-center gap-3 mb-2">
              <PlayCircle size={20} className="text-blue-500" />
              <span className="text-2xl font-bold">{combinedSchedule.filter(a => a.status === 'in_progress').length}</span>
            </div>
            <p className="text-sm text-stone-500">In Progress</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'completed' ? 'ring-2 ring-green-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}
          >
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 size={20} className="text-green-500" />
              <span className="text-2xl font-bold">{combinedSchedule.filter(a => a.status === 'completed').length}</span>
            </div>
            <p className="text-sm text-stone-500">Completed</p>
          </div>
        </div>

        {filteredCombined.length === 0 ? (
          <div className="apple-card p-16 text-center">
            <CalendarDays size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No tasks assigned yet</p>
            <p className="text-sm text-stone-400 mt-1">
              Tasks will appear here when they are assigned to you
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All Bar */}
            <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2 border">
              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={selectedItems.size === filteredCombined.length && filteredCombined.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm text-stone-600">
                  {selectedItems.size > 0 ? `${selectedItems.size} selected` : 'Select All'}
                </span>
              </div>
              {selectedItems.size > 0 && (
                <Button variant="outline" size="sm" onClick={() => setSelectedItems(new Set())}>
                  Clear Selection
                </Button>
              )}
            </div>

            {Object.keys(groupedByProject).map((projectId) => {
              const projectItems = groupedByProject[projectId];
              const isExpanded = expandedProjects.has(projectId);
              const allSelected = isProjectAllSelected(projectId);
              
              return (
                <Collapsible key={projectId} open={isExpanded} onOpenChange={() => toggleProjectExpand(projectId)}>
                  <div className="apple-card overflow-hidden">
                    <div className="bg-white px-4 py-3 border-b flex items-center gap-3">
                      <Checkbox 
                        checked={allSelected}
                        onCheckedChange={() => toggleProjectSelection(projectId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <CollapsibleTrigger className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-stone-50 rounded px-2 py-1">
                        {isExpanded ? <ChevronDown size={18} className="text-stone-500" /> : <ChevronRight size={18} className="text-stone-500" />}
                        <h3 className="font-semibold text-stone-900">{getProjectName(projectId)}</h3>
                        <span className="text-sm text-stone-400">({projectItems.length} items)</span>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent>
                      <div className="divide-y">
                        {projectItems.map((item) => {
                          const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.not_started;
                          const StatusIcon = statusConfig.icon;
                          const { totalDays, start, end } = getTimelinePosition(item);
                          const isTask = item.source === 'task';
                          const taskStatusConfig = isTask ? TASK_STATUS_CONFIG[item.task_status] : null;
                          const canManage = user.role === 'owner' || user.role === 'staff';
                          
                          return (
                            <div key={item.id} className="p-4 md:p-6 group hover:bg-stone-50 transition-colors" data-testid={`item-${item.id}`}>
                              <div className="flex flex-col md:flex-row md:items-center gap-4">
                                {/* Checkbox */}
                                <Checkbox 
                                  checked={selectedItems.has(item.id)}
                                  onCheckedChange={() => toggleItemSelection(item.id)}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    {/* Source Badge */}
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${isTask ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {isTask ? 'Task' : 'Activity'}
                                    </span>
                                    {/* Task name - clickable for tasks */}
                                    {isTask ? (
                                      <button
                                        onClick={() => handleTaskClick(item)}
                                        className="font-semibold text-stone-900 hover:text-stone-800 flex items-center gap-1 group/title"
                                        title="View task details"
                                      >
                                        {item.name}
                                        <ExternalLink size={14} className="opacity-0 group-hover/title:opacity-100 transition-opacity" />
                                </button>
                              ) : (
                                <h4 className="font-semibold text-stone-900">{item.name}</h4>
                              )}
                              {/* Status Badge */}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                                <StatusIcon size={12} />
                                {statusConfig.label}
                              </span>
                              {/* Task approval status */}
                              {isTask && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {item.approved ? 'Approved' : 'Pending Approval'}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-stone-500 mb-2">{item.description}</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-xs text-stone-500">
                              <span className="flex items-center gap-1">
                                <CalendarDays size={12} />
                                {formatDate(start)} - {formatDate(end)} ({totalDays} days)
                              </span>
                              {item.assigned_to && (
                                <span>Assigned: {getUserName(item.assigned_to)}</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Progress Slider - for both tasks and activities */}
                          <div className="w-full md:w-56">
                            <ProgressSlider
                              progress={item.progress || 0}
                              onProgressChange={(newProgress) => handleProgressChange(item, newProgress, isTask)}
                              size="md"
                              showLabels={true}
                            />
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                            {/* Task-specific actions */}
                            {isTask && (
                              <>
                                {/* Comments button */}
                                <button
                                  onClick={() => { 
                                    setSelectedTaskForComments({
                                      id: item.task_id || item.id.replace('task_', ''),
                                      title: item.name,
                                      task_cost: item.task_cost || 0,
                                      approved: item.approved || false,
                                      status: item.status,
                                    }); 
                                    setCommentsSidebarOpen(true); 
                                  }}
                                  className="p-2 text-stone-800 hover:text-stone-800/80 hover:bg-primary/10 rounded-lg"
                                  title="Comments"
                                  data-testid={`task-comments-btn-${item.id}`}
                                >
                                  <MessageSquare size={18} />
                                </button>
                                
                                {/* P0 NEW: Chat with Task Subject (Admin only) */}
                                {isAdmin && (
                                  <button
                                    onClick={() => openChatWithTask(item)}
                                    className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                                    title="Chat about this task"
                                    data-testid={`task-chat-btn-${item.id}`}
                                  >
                                    <Send size={18} />
                                  </button>
                                )}
                                
                                {/* P0 NEW: Assign Multiple Vendors (Admin only) */}
                                {isAdmin && (
                                  <button
                                    onClick={() => openAssignVendorsDialog(item)}
                                    className={`p-2 ${item.assigned_to || (item.assigned_vendors && item.assigned_vendors.length > 0) ? 'text-green-500' : 'text-amber-500'} hover:text-amber-700 hover:bg-amber-50 rounded-lg`}
                                    title={item.assigned_vendors?.length > 1 ? `${item.assigned_vendors.length} vendors assigned` : 'Assign vendors'}
                                    data-testid={`task-assign-vendors-btn-${item.id}`}
                                  >
                                    <Users size={18} />
                                  </button>
                                )}
                                
                                {/* Image upload button */}
                                <button
                                  onClick={() => openImageDialog(item)}
                                  className="p-2 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
                                  title="Upload Image"
                                  data-testid={`task-image-btn-${item.id}`}
                                >
                                  <Camera size={18} />
                                </button>
                                
                                {/* Toggle Approval (for approved tasks - allow revert) */}
                                {canManage && item.approved && (
                                  <button
                                    onClick={() => handleToggleApproval(item)}
                                    className="p-2 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg"
                                    title="Revert to pending approval"
                                  >
                                    <RotateCcw size={18} />
                                  </button>
                                )}
                                
                                {/* Approve button for pending tasks */}
                                {canManage && !item.approved && (
                                  <button
                                    onClick={() => handleToggleApproval(item)}
                                    className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg"
                                    title="Approve task"
                                  >
                                    <CheckCircle2 size={18} />
                                  </button>
                                )}
                                
                                {/* Navigate to task */}
                                <button
                                  onClick={() => handleTaskClick(item)}
                                  className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                  title="View task details"
                                >
                                  <ExternalLink size={18} />
                                </button>
                              </>
                            )}
                            
                            {/* Activity-specific actions */}
                            {!isTask && (
                              <>
                                {item.status === 'not_started' && (
                                  <button
                                    onClick={() => handleStatusChange(item, 'in_progress')}
                                    className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                                    title="Start"
                                  >
                                    <PlayCircle size={18} />
                                  </button>
                                )}
                                {item.status === 'in_progress' && (
                                  <button
                                    onClick={() => handleStatusChange(item, 'completed')}
                                    className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg"
                                    title="Complete"
                                  >
                                    <CheckCircle2 size={18} />
                                  </button>
                                )}
                                <button
                                  onClick={() => openEditDialog(item)}
                                  className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button
                                  onClick={() => { setSelectedActivity(item); setDeleteDialogOpen(true); }}
                                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => { if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl">
                {editDialogOpen ? 'Edit Activity' : 'Create Activity'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Project *</Label>
                <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {[...projects]
                      .filter(p => isActiveProject(p))
                      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                      .map((p, idx) => <SelectItem key={p.id} value={p.id}>{idx + 1}. {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Activity Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Foundation Work, Electrical Wiring"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Activity details..."
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assign To</Label>
                  <Select value={formData.assigned_to} onValueChange={(v) => setFormData({ ...formData, assigned_to: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={editDialogOpen ? handleEdit : handleCreate} className="apple-button">
                {editDialogOpen ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Activity</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{selectedActivity?.name}&quot;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Task Comments Sidebar */}
        <TaskCommentsSidebar
          task={selectedTaskForComments}
          isOpen={commentsSidebarOpen}
          onClose={() => { setCommentsSidebarOpen(false); setSelectedTaskForComments(null); }}
          onTaskUpdated={fetchData}
        />

        {/* Image Upload Dialog */}
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogContent className="max-w-md" data-testid="image-upload-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <Camera className="text-emerald-500" size={20} />
                Upload Task Image
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedTaskForImage && (
                <p className="text-sm text-stone-500">
                  Uploading image for: <span className="font-medium text-stone-900">{selectedTaskForImage.title}</span>
                </p>
              )}
              
              <div>
                <Label className="text-stone-900">Caption (Optional)</Label>
                <Input
                  placeholder="Describe this image..."
                  className="bg-white text-stone-900 border-stone-300 mt-1"
                  value={imageCaption}
                  onChange={(e) => setImageCaption(e.target.value)}
                  data-testid="image-caption-input"
                />
              </div>

              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileSelect(e, false)}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileSelect(e, true)}
              />

              {/* Upload buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="h-24 flex-col gap-2"
                  data-testid="select-file-btn"
                >
                  <ImageIcon size={28} className="text-stone-500" />
                  <span className="text-sm">Select from Device</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                  className="h-24 flex-col gap-2"
                  data-testid="take-photo-btn"
                >
                  <Camera size={28} className="text-emerald-500" />
                  <span className="text-sm">Take Photo</span>
                </Button>
              </div>

              {uploading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-sm text-stone-500 mt-2">Uploading image...</p>
                </div>
              )}

              <p className="text-xs text-stone-400 text-center">
                Supported formats: JPG, PNG, GIF, WebP (max 5MB)
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setImageDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* P0 NEW: Chat Dialog with Task Subject */}
        <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
          <DialogContent className="max-w-md" data-testid="chat-with-task-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <MessageSquare className="text-blue-500" size={20} />
                Start Chat About Task
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedTaskForChat && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-stone-700">Task Subject:</p>
                  <p className="text-stone-900 font-semibold">{selectedTaskForChat.name}</p>
                  <p className="text-xs text-stone-500 mt-1">Project: {getProjectName(selectedTaskForChat.project_id)}</p>
                </div>
              )}
              
              <div>
                <Label className="text-stone-900">Your Message</Label>
                <Textarea
                  placeholder="Type your message about this task..."
                  className="bg-white text-stone-900 border-stone-300 mt-1 min-h-[100px]"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  data-testid="chat-message-input"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setChatDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
              <Button 
                onClick={handleSendChatMessage} 
                disabled={sendingChat || !chatMessage.trim()}
                className="apple-button"
                data-testid="send-chat-btn"
              >
                {sendingChat ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* P0 NEW: Multi-Vendor Assignment Dialog (Dropdown with checkboxes) */}
        <Dialog open={assignVendorsDialogOpen} onOpenChange={setAssignVendorsDialogOpen}>
          <DialogContent className="max-w-md" data-testid="assign-vendors-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <Users className="text-amber-500" size={20} />
                Assign Vendors to Task
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedTaskForVendors && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-stone-700">Task:</p>
                  <p className="text-stone-900 font-semibold">{selectedTaskForVendors.name}</p>
                  <p className="text-xs text-stone-500 mt-1">Project: {getProjectName(selectedTaskForVendors.project_id)}</p>
                </div>
              )}
              
              {availableVendors.length === 0 ? (
                <div className="text-center py-6 text-stone-500">
                  <Users size={32} className="mx-auto mb-2 text-stone-300" />
                  <p>No vendors assigned to this project.</p>
                  <p className="text-sm">Assign vendors to the project first.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-stone-900 mb-2 block">Select Vendors</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-stone-50">
                    {availableVendors.map(vendor => (
                      <div 
                        key={vendor.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedVendorIds.includes(vendor.id) 
                            ? 'bg-amber-100 border border-amber-300' 
                            : 'bg-white border border-stone-200 hover:bg-stone-100'
                        }`}
                        onClick={() => toggleVendorSelection(vendor.id)}
                        data-testid={`vendor-option-${vendor.id}`}
                      >
                        <Checkbox
                          checked={selectedVendorIds.includes(vendor.id)}
                          onCheckedChange={() => toggleVendorSelection(vendor.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-stone-900">{vendor.name}</p>
                          {vendor.company && (
                            <p className="text-xs text-stone-500">{vendor.company}</p>
                          )}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          {vendor.role}
                        </span>
                      </div>
                    ))}
                  </div>
                  {selectedVendorIds.length > 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      {selectedVendorIds.length} vendor(s) selected
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setAssignVendorsDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
              <Button 
                onClick={handleAssignMultipleVendors} 
                disabled={selectedVendorIds.length === 0}
                className="apple-button"
                data-testid="assign-vendors-btn"
              >
                Assign {selectedVendorIds.length > 0 ? `(${selectedVendorIds.length})` : ''} Vendors
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* P2 NEW: Bulk Vendor Assignment Dialog */}
        <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-assign-vendors-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <Users className="text-amber-500" size={20} />
                Bulk Assign Vendors
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-stone-700">Assigning to:</p>
                <p className="text-stone-900 font-semibold">
                  {filteredCombined.filter(item => selectedItems.has(item.id) && item.source === 'task').length} task(s) selected
                </p>
              </div>
              
              {bulkAvailableVendors.length === 0 ? (
                <div className="text-center py-6 text-stone-500">
                  <Users size={32} className="mx-auto mb-2 text-stone-300" />
                  <p>No vendors available.</p>
                  <p className="text-sm">Assign vendors to the project(s) first.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-stone-900 mb-2 block">Select Vendors</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-stone-50">
                    {bulkAvailableVendors.map(vendor => (
                      <div 
                        key={vendor.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          bulkSelectedVendorIds.includes(vendor.id) 
                            ? 'bg-amber-100 border border-amber-300' 
                            : 'bg-white border border-stone-200 hover:bg-stone-100'
                        }`}
                        onClick={() => toggleBulkVendorSelection(vendor.id)}
                        data-testid={`bulk-vendor-option-${vendor.id}`}
                      >
                        <Checkbox
                          checked={bulkSelectedVendorIds.includes(vendor.id)}
                          onCheckedChange={() => toggleBulkVendorSelection(vendor.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-stone-900">{vendor.name}</p>
                          {vendor.company && (
                            <p className="text-xs text-stone-500">{vendor.company}</p>
                          )}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          {vendor.role}
                        </span>
                      </div>
                    ))}
                  </div>
                  {bulkSelectedVendorIds.length > 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      {bulkSelectedVendorIds.length} vendor(s) will be assigned to all selected tasks
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setBulkAssignDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
              <Button 
                onClick={handleBulkAssignVendors} 
                disabled={bulkSelectedVendorIds.length === 0}
                className="apple-button"
                data-testid="bulk-assign-confirm-btn"
              >
                Assign to {filteredCombined.filter(item => selectedItems.has(item.id) && item.source === 'task').length} Tasks
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
